'use client'

import { Home, SearchCheck, Bot, Handshake, FileSearch, Binoculars, UserSearch } from "lucide-react"
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { cn } from "@/lib/utils"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar"

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
  } from "@/components/ui/collapsible"
import PREFIX_URL from "@/lib/vars";

export function AppSidebar() {
  const pathname = usePathname();
  const [userInfo, setUserInfo] = useState<{ username: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await fetch(`${PREFIX_URL}/api/get-user`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch user info');
        const data = await response.json();
        setUserInfo(data);
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Access control data
  const ACCESS_VIEWS = [
    {
      "username": "kanav",
      "access_views":[
        {"view": "/scheduling-reviewer", "name": "Scheduling reviewer"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "shubhodeep",
      "access_views":[
        {"view": "/scheduling-reviewer", "name": "Scheduling reviewer"},
        {"view": "reviewer/expense-anomaly-detector", "name": "Expense anomaly detector"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "sandeep",
      "access_views":[
        {"view": "/bank-recon", "name": "Bank Reconciliation"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "naveen",
      "access_views":[
        {"view": "/guilders/bank-reco", "name": "Bank Reconciliation"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "nayan",
      "access_views":[
        {"view": "/hawai-review/Reviewer-task1", "name": "Reviewer Task 1"},
        {"view": "/hawai-review/Reviewer-task2", "name": "Reviewer Task 2"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "kevin",
      "access_views": [{"view": "master", "name": "Master Access"}]
    },
    {
      "username": "tunisha",
      "access_views": [{"view": "master", "name": "Master Access"}]
    },
    {
      "username": "vaibhav",
      "access_views": [{"view": "master", "name": "Master Access"}]
    },
    {
      "username": "abe",
      "access_views":[
        {"name": "Expense categoristaion", "view": "/categorisation-underwriting"},
        {"name": "OM abstract", "view": "/underwriting"},
        {"name": "Find red flags", "view": "/underwriting/red-flag"},
        {"name": "Find comparables", "view": "/underwriting/find-comparables"},
        {"name": "Detective", "view": "/underwriting/detective"},
        {"view": "/ap-bot", "name": "AP Bot"},
        {"view": "/lease-abstract", "name": "Lease Abstract"},
        {"view": "/usecases", "name": "Find Usecases"}
      ]
    },
    {
      "username": "ansh",
      "access_views": [{"view": "master", "name": "Master Access"}]
    },
    {
      "username": "ridgeline",
      access_views: [
        { view: "/ridgeline", name: "Financial Analysis & Strategic Recommendations" }
      ]
    },
    {
      username: "guilders-demo",
      access_views: [
        { view: "/guilders/roadmap", name: "RoadMap" },
        { view: "/guilders/review-checklist", name: "Review Checklist" },
        { view: "/guilders/upload-initial-checks", name: "Upload & Initial Checks" },
        { view: "/guilders/bank-reco", name: "Cash & Bank Reconciliation" },
        { view: "/guilders/escrow-loc", name: "Escrow & Letters of Credit" },
        { view: "/guilders/receivables-review", name: "Receivables Review" },
        { view: "/guilders/prepaid-accounts", name: "Prepaid Accounts" },
        { view: "/guilders/utility-vendor-balances", name: "Utility & Vendor Balances" },
        { view: "/guilders/deposits-debt", name: "Deposits & Debt" },
        { view: "/guilders/fee-income-reconciliation", name: "Fees & Income Reconciliation" },
        { view: "/guilders/taxes-and-fixed-assets", name: "Taxes & Fixed Assets" },
        { view: "/guilders/accruals-review/red-flag", name: "Red Flags"  },
        { view: "/guilders/accruals-review/GL", name: "GL_Review"  }
      ]
    }
  ];
  const allNestedItems = [
    {
      title: "Reviewer",
      icon: SearchCheck,
      defaultOpen: true,
      subItems: [
        {
          title: "Scheduling reviewer",
          url: "/scheduling-reviewer"
        },
        {
          title: "Expense anomaly detector",
          url: "reviewer/expense-anomaly-detector"
        },
        {
          title: "Bank Reconciliation",
          url: "/bank-recon"
        }
      ]
    },
    {
      title: "Underwriting",
      icon: Handshake,
      defaultOpen: true,
      subItems: [
        {
          title: "Expense categoristaion",
          url: "/categorisation-underwriting"
        },
        {
          title: "OM abstract",
          url: "/underwriting"
        },
        {
          title: "Find red flags",
          url: "/underwriting/red-flag"
        },
        {
          title: "Find comparables",
          url: "/underwriting/find-comparables"
        },
        {
          title: "Detective",
          url: "/underwriting/detective"
        }
      ]
    },
    {
      title: "Hawai Review",
      icon: SearchCheck,
      defaultOpen: true,
      subItems: [
        {
          title: "Reviewer Task 1",
          url: "/hawai-review/Reviewer-task1"
          
        },
        {
          title: "Reviewer Task 2",
          url: "/hawai-review/Reviewer-task2"
        }
      ]
    },
    {
      title:"Ridgeline",
      icon: Handshake,
      defaultOpen: true,
      subItems: [
        {
          title: "Financial Analysis & Strategic Recommendations",
          url: "/ridgeline"
        }
      ]

    },
    {
      title : "60 Guilders",
      icon: Handshake,
      defaultOpen: true,
      subItems: [
        {
          title: "RoadMap",
          url: "/guilders/roadmap"
        },
        {
          title: "Review Checklist",
          url: "/guilders/review-checklist"
        },
        {
          title: "Upload & Initial Checks",
          url: "/guilders/upload-initial-checks"
        },
        {
          title: "Cash & Bank Reconciliation",
          url: "/guilders/bank-reco"
        },
        {
          title: "Escrow & Letters of Credit",
          url: "/guilders/escrow-loc"
        },
        {
          title: "Receivables Review",
          url: "/guilders/receivables-review"
        },
        {
          title: "Prepaid Accounts",
          url: "/guilders/prepaid-accounts"
        },
        {
          title: "Utility & Vendor Balances",
          url: "/guilders/utility-vendor-balances"
        },
        {
          title: "Deposits & Debt",
          url: "/guilders/deposits-debt"
        },
        {
          title: "Fees & Income Reconciliation",
          url: "/guilders/fee-income-reconciliation"
        },
        {
          title: "Taxes & Fixed Assets",
          url: "/guilders/taxes-and-fixed-assets"
        },
        {
          title: "Accruals Review",
          subItems: [
        {
          title: "Red Flags",
          url: "/guilders/accruals-review/red-flag"
        },
        {
          title: "GL_Review",
          url: "/guilders/accruals-review/GL"
          
        }
      ]
          
        }
      ]
    }
  ];


  // Helper function to check if user has access to a specific view
  const hasAccess = (url: any) => {
    // Always allow access to home page
    if (url === "/dashboard") return true;
    
    if (!userInfo?.username) return false;
    
    const userAccess = ACCESS_VIEWS.find(user => user.username === userInfo.username);
    
    if (!userAccess) return false;
    
    // Handle different property names in your data
    const accessViews = userAccess.access_views || userAccess.access_views || userAccess.access_views || [];
    
    // Check if user has master access (grants access to everything)
    const hasMasterAccess = accessViews.some(item => item.view === "master");
    if (hasMasterAccess) {
      // Master access users can see everything plus these specific views
      const masterViews = ["/ap-bot", "/lease-abstract", "/usecases"];
      // Return true for master views or any other view since they have full access
      return masterViews.includes(url) || accessViews.some(item => item.view === url) || true;
    }
    
    // Otherwise check specific URL access
    return accessViews.some(item => item.view === url);
  };

  // Non-nested items (always visible)
  const simpleItems = [
    {
      title: "Home",
      url: "/dashboard",
      icon: Home,
    },
    {
      title: "AP Bot",
      url: "/ap-bot",
      icon: Bot,
    },
    {
      title: "Lease Abstract",
      url: "/lease-abstract",
      icon: FileSearch
    },
    {
      title: "Find Usecases",
      url: "/usecases",
      icon: Binoculars,
    },
    {
      title: "CRM PRO",
      url: "/crm-pro",
      icon: UserSearch,
    }
  ];

  // Filter simple items based on user access
  const filteredSimpleItems = simpleItems.filter(item => hasAccess(item.url));

  // Filter nested items based on user access
  const nestedItems = allNestedItems.map(section => ({
    ...section,
    subItems: section.subItems.filter(subItem => hasAccess(subItem.url))
  })).filter(section => section.subItems.length > 0); // Only show sections that have accessible items

  // All possible nested items with access control
  
  if (isLoading) return null;
  if (pathname === '/login' || pathname === '/') return null;

  return (
  
    <Sidebar collapsible="icon" variant="inset" className="bg-[#24497A] text-white p-1">
      <SidebarContent>
        <div className="flex flex-col items-center py-4">
          <img src="/rekpo-logo.png" alt="REKPO.ai Logo" className="mb-2 max-w-[160px] w-full h-auto" />
        </div>
        <SidebarGroup>
          <SidebarGroupLabel className="text-white/70 tracking-widest uppercase text-xs font-bold mb-2">
            Application
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredSimpleItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    className={cn(
                      "transition-all duration-150 rounded-lg px-4 py-2 text-gray-800 hover:bg-[#FF7A00] hover:text-white",
                      pathname === item.url && "bg-[#FF7A00] text-white"
                    )}
                  >
                    <a href={item.url} className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      <span className="font-semibold text-base group-data-[collapsible=icon]:hidden">{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>

            {nestedItems.map((section) => (
              <SidebarMenu key={section.title}>
                <Collapsible defaultOpen={section.defaultOpen}>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton className="font-semibold text-base text-gray-800 hover:bg-[#FF7A00] hover:text-white px-4">
                        <section.icon className="w-5 h-5" />
                        <span className="group-data-[collapsible=icon]:hidden">{section.title}</span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {section.subItems.map((subItem) =>
                        subItem.subItems ? (
                          <SidebarMenuSub key={subItem.title}>
                            <Collapsible defaultOpen>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuSubButton>
                                  <span>{subItem.title}</span>
                                </SidebarMenuSubButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                {subItem.subItems.map((nestedItem) => (
                                  <SidebarMenuSubItem key={nestedItem.title}>
                                    <a
                                      href={nestedItem.url}
                                      className={cn(
                                        "block px-4 py-1 text-gray-800 hover:bg-[#FF7A00] hover:text-white rounded-lg transition-all duration-150",
                                        pathname === nestedItem.url && "bg-[#FF7A00] text-white"
                                      )}
                                    >
                                      {nestedItem.title}
                                    </a>
                                  </SidebarMenuSubItem>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          </SidebarMenuSub>
                        ) : (
                          <SidebarMenuSub key={subItem.title}>
                            <SidebarMenuSubItem>
                              <a
                                href={subItem.url}
                                className={cn(
                                  "block px-4 py-1 text-gray-800 hover:bg-[#FF7A00] hover:text-white rounded-lg transition-all duration-150",
                                  pathname === subItem.url && "bg-[#FF7A00] text-white"
                                )}
                              >
                                {subItem.title}
                              </a>
                            </SidebarMenuSubItem>
                          </SidebarMenuSub>
                        )
                      )}
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            ))}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}